<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="cashback-activation-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.cashbackActivation.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="cashback-activation-page-title"
        />
        <AtomText
          class="mt-1"
          :text="t('subscription.cashbackActivation.description')"
          color-variant="description"
          data-cy="cashback-activation-page-description"
        />

        <div class="mt-2 flex items-center justify-center gap-3">
          <MoleculeTag
            :value="t('subscription.cashbackActivation.tags.freeOption')"
            type="secondary"
            size="lg"
            data-cy="cashback-activation-tag-free-option"
          />
          <MoleculeTag
            :value="t('subscription.cashbackActivation.tags.withoutEngagement')"
            type="secondary"
            size="lg"
            data-cy="cashback-activation-tag-without-engagement"
          />
        </div>
      </div>

      <MoleculeFrameCard
        class="mt-8"
        content-theme="success"
        spacing="sm"
        data-cy="cashback-activation-hero-card"
      >
        <div class="flex items-center gap-1">
          <AtomPicto name="jackpot" size="md" data-cy="cashback-activation-hero-picto" />
          <div class="flex flex-col gap-1">
            <AtomText
              :text="
                activeEquipmentControllableCount > 1
                  ? t('subscription.cashbackActivation.heroCard.titlePlural')
                  : t('subscription.cashbackActivation.heroCard.titleSingular')
              "
              font-size="text-sm"
              font-style="font-medium"
              data-cy="cashback-activation-hero-label"
            />
            <div class="flex items-baseline gap-1">
              <AtomText
                is-tag="span"
                font-size="text-3xl"
                font-style="font-bold"
                :text="`+${annualCashbackTotal}€`"
                data-cy="cashback-activation-hero-amount"
              />
              <AtomText
                is-tag="span"
                font-size="text-sm"
                font-style="font-medium"
                :text="t('subscription.cashbackActivation.heroCard.perYear')"
              />
            </div>
          </div>
        </div>
      </MoleculeFrameCard>

      <div class="mt-12 flex flex-col gap-2">
        <AtomText
          :text="t('subscription.cashbackActivation.details.title')"
          font-style="font-medium"
          font-size="text-base"
          data-cy="cashback-activation-details-title"
        />

        <MoleculeFrameCard
          :header-theme="hasAirToWaterHeatPump ? 'light' : 'dark'"
          :content-theme="hasElectricWaterHeater ? 'light' : 'dark'"
          :footer-theme="hasElectricVehicleCharger ? 'light' : 'dark'"
          spacing="sm"
          data-cy="cashback-activation-details-card"
        >
          <template #header>
            <div class="flex items-center gap-3" data-cy="cashback-activation-heat-pump-row">
              <AtomIcon
                icon-name="AirToWaterHeatPumpIcon"
                icon-type="outline"
                :color-variant="hasAirToWaterHeatPump ? 'default' : 'disabled'"
              />
              <div class="flex flex-col flex-1 min-w-0">
                <AtomText
                  :text="t('subscription.cashbackActivation.details.heatPump.name')"
                  font-size="text-sm"
                  font-style="font-medium"
                  :color-variant="hasAirToWaterHeatPump ? 'default' : 'description'"
                />
                <AtomText
                  :text="t('subscription.cashbackActivation.details.heatPump.rate')"
                  font-size="text-xs"
                  color-variant="description"
                />
              </div>
              <MoleculeTag
                v-if="hasAirToWaterHeatPump"
                :value="t('subscription.cashbackActivation.details.heatPump.amount')"
                type="success"
                size="lg"
                data-cy="cashback-activation-heat-pump-tag"
              />
              <AtomText
                v-else
                font-size="text-sm"
                color-variant="label-disabled"
                data-cy="cashback-activation-heat-pump-tag"
              >
                {{ t('subscription.cashbackActivation.details.heatPump.amount') }}
              </AtomText>
            </div>
          </template>

          <div
            class="flex items-center gap-3"
            :class="hasElectricWaterHeater ? 'bg-white' : 'bg-gray-50'"
            data-cy="cashback-activation-water-heater-row"
          >
            <AtomIcon
              icon-name="WaterHeaterIcon"
              icon-type="outline"
              :color-variant="hasElectricWaterHeater ? 'default' : 'disabled'"
            />
            <div class="flex flex-col flex-1 min-w-0">
              <AtomText
                :text="t('subscription.cashbackActivation.details.waterHeater.name')"
                font-size="text-sm"
                font-style="font-medium"
                :color-variant="hasElectricWaterHeater ? 'default' : 'description'"
              />
              <AtomText
                :text="t('subscription.cashbackActivation.details.waterHeater.rate')"
                font-size="text-xs"
                color-variant="description"
              />
            </div>
            <MoleculeTag
              v-if="hasElectricWaterHeater"
              :value="t('subscription.cashbackActivation.details.waterHeater.amount')"
              type="success"
              size="lg"
              data-cy="cashback-activation-water-heater-tag"
            />
            <AtomText
              v-else
              font-size="text-sm"
              color-variant="label-disabled"
              data-cy="cashback-activation-water-heater-tag"
            >
              {{ t('subscription.cashbackActivation.details.waterHeater.amount') }}
            </AtomText>
          </div>
          <template #footer>
            <div class="flex items-center gap-3" data-cy="cashback-activation-charger-row">
              <AtomIcon
                icon-name="ChargingStationIcon"
                icon-type="outline"
                :color-variant="hasElectricVehicleCharger ? 'default' : 'disabled'"
              />
              <div class="flex flex-col flex-1 min-w-0">
                <AtomText
                  :text="t('subscription.cashbackActivation.details.charger.name')"
                  font-size="text-sm"
                  font-style="font-medium"
                  :color-variant="hasElectricVehicleCharger ? 'default' : 'description'"
                />
                <AtomText
                  :text="t('subscription.cashbackActivation.details.charger.rate')"
                  font-size="text-xs"
                  color-variant="description"
                />
              </div>
              <MoleculeTag
                v-if="hasElectricVehicleCharger"
                :value="t('subscription.cashbackActivation.details.charger.amount')"
                type="success"
                size="lg"
                data-cy="cashback-activation-charger-tag"
              />
              <AtomText
                v-else
                font-size="text-sm"
                color-variant="label-disabled"
                data-cy="cashback-activation-charger-tag"
              >
                {{ t('subscription.cashbackActivation.details.charger.amount') }}
              </AtomText>
            </div>
          </template>
        </MoleculeFrameCard>

        <OrganismAlert
          v-if="!hasElectricVehicleCharger"
          :title="t('subscription.cashbackActivation.details.alert')"
          has-icon
          state="info"
          data-cy="cashback-activation-charger-alert"
        />

        <MoleculeStandaloneLink
          :label="t('subscription.cashbackActivation.details.link')"
          type="url"
          :to="t('subscription.cashbackActivation.details.linkUrl')"
          target="blank"
          font-size="sm"
          icon-name="ExternalLinkIcon"
          color-variant="brand"
          data-cy="cashback-activation-pilotage-link"
        />
      </div>

      <AtomDivider class="mt-12 mb-12" />

      <div data-cy="cashback-activation-form">
        <AtomText
          :text="t('subscription.cashbackActivation.form.label')"
          font-size="text-sm"
          font-style="font-semibold"
          data-cy="cashback-activation-form-label"
        />

        <div class="flex flex-col gap-4 mt-2" data-cy="cashback-activation-radio-group">
          <div
            class="flex gap-3 items-start cursor-pointer"
            data-cy="cashback-activation-accept-row"
          >
            <AtomRadio
              id="cashback-choice-accept"
              name="cashback-choice"
              :value="true"
              :checked="hasAcceptedEquipmentControl === true"
              :has-error="isSubmitted && !hasChosenEquipmentControl"
              class="mt-0.5 shrink-0"
              @update:model-value="setAcceptedEquipmentControl(true)"
            />
            <div class="flex flex-col gap-1">
              <AtomText
                is-tag="label"
                for="cashback-choice-accept"
                label-for="cashback-choice-accept"
                class="cursor-pointer"
                data-cy="cashback-activation-accept-label"
              >
                <AtomText
                  is-tag="span"
                  font-size="text-sm"
                  font-style="font-medium"
                  :text="tm('subscription.cashbackActivation.form.accept.text')[0]"
                  :color-variant="isSubmitted && !hasChosenEquipmentControl ? 'error' : 'label'"
                /><MoleculeStandaloneLink
                  :label="t('subscription.cashbackActivation.form.accept.conditionsLink')"
                  type="route"
                  font-size="sm"
                  font-weight="semibold"
                  @click.prevent="isModalOpen = true"
                  :color-variant="isSubmitted && !hasChosenEquipmentControl ? 'error' : 'default'"
                  data-cy="cashback-activation-conditions-link"
                  class="!inline !flex-none"
                /><AtomText
                  is-tag="span"
                  font-size="text-sm"
                  font-style="font-medium"
                  :text="tm('subscription.cashbackActivation.form.accept.text')[1]"
                  :color-variant="isSubmitted && !hasChosenEquipmentControl ? 'error' : 'label'"
                />
              </AtomText>
            </div>
          </div>

          <div
            class="flex gap-3 items-center cursor-pointer"
            data-cy="cashback-activation-decline-row"
          >
            <AtomRadio
              id="cashback-choice-decline"
              name="cashback-choice"
              :value="false"
              :checked="hasAcceptedEquipmentControl === false"
              :has-error="isSubmitted && !hasChosenEquipmentControl"
              @update:model-value="setAcceptedEquipmentControl(false)"
            />
            <AtomText
              is-tag="label"
              font-size="text-sm"
              font-style="font-medium"
              for="cashback-choice-decline"
              class="cursor-pointer"
              :text="t('subscription.cashbackActivation.form.decline')"
              :color-variant="isSubmitted && !hasChosenEquipmentControl ? 'error' : 'label'"
              data-cy="cashback-activation-decline-label"
            />
          </div>

          <AtomText
            v-if="isSubmitted && !hasChosenEquipmentControl"
            :text="t('subscription.cashbackActivation.form.error')"
            color-variant="error"
            font-size="text-sm"
            data-cy="cashback-activation-form-error"
          />
        </div>
      </div>
      <CashbackActivationConditionsModal
        v-model:is-open="isModalOpen"
        data-cy="cashback-activation-conditions-modal"
      />
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import { getErrorToasterConfig } from '@/utils/error.util';

import CashbackActivationConditionsModal from '@/components/CashbackActivationConditionsModal.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import {
  AIR_TO_WATER_HEAT_PUMP_ANNUAL_CASHBACK,
  ELECTRIC_WATER_HEATER_ANNUAL_CASHBACK,
  EV_CHARGER_ANNUAL_CASHBACK,
} from '@/constants/subscription.constants';

const { t, tm } = useI18n();

const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();
const toastSnackBar = useToastSnackbarStore();

const { goToNextStep } = stepStore;

const {
  isSaveSubscriptionInProgress,
  isGetSubscriptionInProgress,
  getCurrentSubscriptionOptions,
  hasAirToWaterHeatPump,
  hasElectricWaterHeater,
  hasElectricVehicleCharger,
} = storeToRefs(subscriptionStore);

const isSubmitted = ref(false);
const isModalOpen = ref(false);

const activeEquipmentControllableCount = computed(() => {
  let equipments = 0;

  if (hasAirToWaterHeatPump.value) {
    equipments++;
  }
  if (hasElectricWaterHeater.value) {
    equipments++;
  }
  if (hasElectricVehicleCharger.value) {
    equipments++;
  }

  return equipments;
});

const annualCashbackTotal = computed(() => {
  let amount = 0;
  if (hasAirToWaterHeatPump.value) {
    amount += AIR_TO_WATER_HEAT_PUMP_ANNUAL_CASHBACK;
  }
  if (hasElectricWaterHeater.value) {
    amount += ELECTRIC_WATER_HEATER_ANNUAL_CASHBACK;
  }
  if (hasElectricVehicleCharger.value) {
    amount += EV_CHARGER_ANNUAL_CASHBACK;
  }
  return amount;
});

const hasChosenEquipmentControl = ref<boolean>(
  getCurrentSubscriptionOptions.value?.equipmentControl?.requested !== null
);

const hasAcceptedEquipmentControl = ref<boolean | null>(
  getCurrentSubscriptionOptions.value?.equipmentControl?.requested ?? null
);

const setAcceptedEquipmentControl = (value: boolean) => {
  hasAcceptedEquipmentControl.value = value;
  hasChosenEquipmentControl.value = true;
};

const onSubmit = async () => {
  isSubmitted.value = true;

  if (!hasChosenEquipmentControl.value || hasAcceptedEquipmentControl.value === null) {
    return;
  }

  try {
    await subscriptionStore.updateSubscriptionWithEquipmentControl(
      hasAcceptedEquipmentControl.value
    );
    goToNextStep();
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'cashback-activation-error',
      defaultMessage: t(
        'subscription.cashbackActivation.api.errors.patchSubscriptionEquipmentControlOptions'
      ),
    });
    toastSnackBar.addToast(toastConfig);
  }
};
</script>

<style scoped></style>
